import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  ChevronDown,
  Download,
  FileDown,
  FileSpreadsheet,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ReportFiltersProps {
  children: ReactNode;
  className?: string;
}

interface DateRangeInputsProps {
  start: string;
  end: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

interface ReportSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

interface ReportTextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}

export function ReportFilters({ children, className }: ReportFiltersProps) {
  return <div className={cn('flex flex-wrap gap-3 mb-5', className)}>{children}</div>;
}

export function DateRangeInputs({ start, end, onStartChange, onEndChange }: DateRangeInputsProps) {
  function clampStart(value: string) {
    if (value && end && value > end) onEndChange(value);
  }

  function clampEnd(value: string) {
    if (value && start && value < start) onStartChange(value);
  }

  return (
    <div className='flex items-center gap-2 p-1 rounded-xl border border-border/50 bg-card'>
      <Input
        type='date'
        value={start}
        max={end}
        onChange={(event) => {
          onStartChange(event.target.value);
          clampStart(event.target.value);
        }}
        onBlur={(event) => clampStart(event.target.value)}
        className='border-0 bg-transparent shadow-none focus-visible:ring-0 h-auto px-3 py-1.5 cursor-pointer'
      />
      <span className='text-muted-foreground text-xs'>-</span>
      <Input
        type='date'
        value={end}
        min={start}
        onChange={(event) => {
          onEndChange(event.target.value);
          clampEnd(event.target.value);
        }}
        onBlur={(event) => clampEnd(event.target.value)}
        className='border-0 bg-transparent shadow-none focus-visible:ring-0 h-auto px-3 py-1.5 cursor-pointer'
      />
    </div>
  );
}

export function ReportSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
}: ReportSelectProps) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'appearance-none px-3 py-2 rounded-xl border border-border/50 bg-card text-sm text-foreground outline-none focus:border-primary/50 cursor-pointer',
        className
      )}
    >
      {placeholder && <option value=''>{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function ReportTextInput({ value, onChange, placeholder, className }: ReportTextInputProps) {
  return (
    <Input
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'border-border/50 bg-card focus-visible:ring-0 focus-visible:border-primary/50',
        className ?? 'w-44'
      )}
    />
  );
}

export function ReportLoadButton({
  isLoading,
  onClick,
}: {
  isLoading: boolean;
  onClick: () => void;
}) {
  return (
    <Button onClick={onClick} disabled={isLoading} className='gap-2'>
      {isLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <Search className='w-4 h-4' />}
      Загрузить
    </Button>
  );
}

export function ReportExportButton({
  className,
  label = 'Экспорт',
  onClick,
}: {
  className?: string;
  label?: string;
  onClick?: () => void;
}) {
  return (
    <Button variant='outline' onClick={onClick} className={cn('gap-2', className)}>
      <Download className='w-4 h-4' /> {label}
    </Button>
  );
}

function ExportErrorToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  const isAccessError = /доступ|access|403|разрешени/i.test(message);

  useEffect(() => {
    const timer = setTimeout(onDismiss, 7000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={{ duration: 0.2 }}
      className='fixed bottom-6 right-6 z-50 flex items-start gap-3 w-96 rounded-2xl border border-red-500/30 bg-card p-4 shadow-xl shadow-black/20'
    >
      <AlertCircle className='w-4 h-4 text-red-400 shrink-0 mt-0.5' />
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-foreground'>Ошибка экспорта</p>
        <p className='text-xs text-muted-foreground mt-0.5 leading-relaxed'>{message}</p>
        {isAccessError && (
          <p className='text-xs text-primary mt-2 leading-relaxed'>
            Запросите доступ у администратора Google Workspace.
          </p>
        )}
      </div>
      <Button
        variant='ghost'
        size='icon'
        onClick={onDismiss}
        className='shrink-0 w-6 h-6 text-muted-foreground hover:text-foreground'
      >
        <X className='w-3.5 h-3.5' />
      </Button>
    </motion.div>
  );
}

export function ReportExportMenu({
  className,
  onExcelExport,
  onGoogleExport,
}: {
  className?: string;
  onExcelExport: () => Promise<void> | void;
  onGoogleExport: () => Promise<void> | void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState<'excel' | 'google' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!exportMenuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  async function handleExport(type: 'excel' | 'google') {
    const action = type === 'excel' ? onExcelExport : onGoogleExport;
    setExporting(type);
    setExportError(null);
    setIsOpen(false);

    try {
      await action();
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Не удалось выполнить экспорт');
    } finally {
      setExporting(null);
    }
  }

  const isBusy = exporting !== null;

  return (
    <>
      <div ref={exportMenuRef} className={cn('relative', className)}>
        <Button
          variant='outline'
          onClick={() => setIsOpen((value) => !value)}
          disabled={isBusy}
          className='gap-2'
        >
          {isBusy ? <Loader2 className='w-4 h-4 animate-spin' /> : <Download className='w-4 h-4' />}
          Экспорт
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </Button>

        {isOpen && (
          <div className='absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-border/50 bg-card shadow-xl shadow-black/10'>
            <Button
              variant='ghost'
              onClick={() => void handleExport('excel')}
              className='w-full justify-start gap-3 px-3 h-auto py-2.5 rounded-none'
            >
              <FileDown className='w-4 h-4 text-muted-foreground' />
              Excel
            </Button>
            <Button
              variant='ghost'
              onClick={() => void handleExport('google')}
              className='w-full justify-start gap-3 px-3 h-auto py-2.5 rounded-none'
            >
              <FileSpreadsheet className='w-4 h-4 text-muted-foreground' />
              Google Sheets
            </Button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {exportError && (
          <ExportErrorToast message={exportError} onDismiss={() => setExportError(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
